/* ==========================================================================
   procedural-coral.js
   Procedural 3D coral colonies — real triangle geometry, no voxels.

   Vendored verbatim apart from the ESM export at the foot of the file. Do not
   reformat: it is maintained elsewhere, and keeping it byte-comparable makes
   re-syncing an update a diff rather than an archaeology exercise.

   No dependencies. Generation is pure math, so it runs in Node as happily as
   in a browser. THREE is only touched by the optional toBufferGeometry()
   helper, which you pass in yourself.
   ========================================================================== */
'use strict';

/* ---------- scalar math ---------- */
const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------- vectors ---------- */
const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = a => Math.hypot(a[0], a[1], a[2]);
function norm(a) { const l = len(a); return l > 1e-9 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 1, 0]; }
function perp(t) {
  const a = Math.abs(t[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  return norm(cross(a, t));
}

/* ---------- 3D value noise ---------- */
function makeNoise3(seed) {
  const rnd = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = rnd();
  function h3(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = ((n ^ (n >> 13)) * 1274126177) | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const x00 = lerp(h3(xi, yi, zi), h3(xi + 1, yi, zi), u);
    const x10 = lerp(h3(xi, yi + 1, zi), h3(xi + 1, yi + 1, zi), u);
    const x01 = lerp(h3(xi, yi, zi + 1), h3(xi + 1, yi, zi + 1), u);
    const x11 = lerp(h3(xi, yi + 1, zi + 1), h3(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(x00, x10, v), lerp(x01, x11, v), w);
  };
}
function makeFbm3(nz) {
  return function (p, freq, oct, gain) {
    let a = 1, f = freq, s = 0, n = 0;
    oct = oct || 3; gain = gain === undefined ? 0.5 : gain;
    for (let i = 0; i < oct; i++) {
      s += a * (nz(p[0] * f, p[1] * f, p[2] * f) - 0.5) * 2;
      n += a; a *= gain; f *= 2.03;
    }
    return s / n;
  };
}

/* ---------- colour ---------- */
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const mulc = (c, k) => [c[0] * k, c[1] * k, c[2] * k];

const PALETTES = {
  branching: { base: [0.455, 0.330, 0.208], tip: [0.930, 0.855, 0.720], accent: [0.560, 0.430, 0.640] },
  table:     { base: [0.660, 0.480, 0.250], tip: [0.880, 0.750, 0.480], accent: [0.470, 0.340, 0.560] },
  brain:     { base: [0.320, 0.360, 0.170], tip: [0.800, 0.740, 0.470], accent: [0.230, 0.270, 0.130] },
  massive:   { base: [0.545, 0.375, 0.150], tip: [0.720, 0.560, 0.270], accent: [0.360, 0.250, 0.110] },
  foliose:   { base: [0.720, 0.410, 0.190], tip: [0.940, 0.720, 0.340], accent: [0.470, 0.230, 0.130] },
  fan:       { base: [0.400, 0.140, 0.230], tip: [0.760, 0.400, 0.480], accent: [0.290, 0.100, 0.190] }
};
const bleachC = (c, t) => mixc(c, mixc([0.955, 0.945, 0.925], c, 0.10), t);

/* ========================================================================== */
function Builder() { this.p = []; this.c = []; this.t = []; this.i = []; }
Builder.prototype.vert = function (p, col, u, v) {
  this.p.push(p[0], p[1], p[2]);
  this.c.push(col[0], col[1], col[2]);
  this.t.push(u, v);
  return (this.p.length / 3) - 1;
};
Builder.prototype.tri = function (a, b, c) { this.i.push(a, b, c); };
Builder.prototype.quad = function (a, b, c, d) { this.i.push(a, b, c, a, c, d); };
Builder.prototype.finish = function () {
  const P = new Float32Array(this.p), I = new Uint32Array(this.i);
  const N = new Float32Array(P.length);
  for (let k = 0; k < I.length; k += 3) {
    const a = I[k] * 3, b = I[k + 1] * 3, c = I[k + 2] * 3;
    const e1 = [P[b] - P[a], P[b + 1] - P[a + 1], P[b + 2] - P[a + 2]];
    const e2 = [P[c] - P[a], P[c + 1] - P[a + 1], P[c + 2] - P[a + 2]];
    const n = cross(e1, e2);
    N[a] += n[0]; N[a + 1] += n[1]; N[a + 2] += n[2];
    N[b] += n[0]; N[b + 1] += n[1]; N[b + 2] += n[2];
    N[c] += n[0]; N[c + 1] += n[1]; N[c + 2] += n[2];
  }
  for (let k = 0; k < N.length; k += 3) {
    const l = Math.hypot(N[k], N[k + 1], N[k + 2]) || 1;
    N[k] /= l; N[k + 1] /= l; N[k + 2] /= l;
  }
  return { positions: P, normals: N, colors: new Float32Array(this.c),
           uvs: new Float32Array(this.t), indices: I };
};

/* ---------- swept tube, rotation-minimising frames ---------- */
function tube(B, path, o) {
  const N = o.radial, rings = [];
  let prevN = null, arc = 0;
  for (let i = 0; i < path.length; i++) {
    const p = path[i].p;
    let t;
    if (i === 0) t = sub(path[1].p, p);
    else if (i === path.length - 1) t = sub(p, path[i - 1].p);
    else t = sub(path[i + 1].p, path[i - 1].p);
    t = norm(t);
    if (!prevN) prevN = perp(t);
    else {
      let n = sub(prevN, mul(t, dot(prevN, t)));
      prevN = len(n) < 1e-7 ? perp(t) : norm(n);
    }
    const n = prevN, b = cross(t, n);
    if (i > 0) arc += len(sub(p, path[i - 1].p));
    const ring = [];
    for (let k = 0; k < N; k++) {
      const ang = k / N * TAU, ca = Math.cos(ang), sa = Math.sin(ang);
      const dir = add(mul(n, ca), mul(b, sa));
      let r = path[i].r;
      if (o.bump > 0) {
        const probe = add(p, mul(dir, r));
        r *= 1 + o.bump * o.fbm(probe, o.bumpFreq, 2, 0.5);
      }
      ring.push(B.vert(add(p, mul(dir, r)), path[i].c, k / N, arc * o.uvScale));
    }
    rings.push(ring);
  }
  for (let i = 0; i < rings.length - 1; i++)
    for (let k = 0; k < N; k++)
      B.quad(rings[i][k], rings[i][(k + 1) % N], rings[i + 1][(k + 1) % N], rings[i + 1][k]);
  const last = path[path.length - 1];
  const tdir = norm(sub(last.p, path[path.length - 2].p));
  const capC = B.vert(add(last.p, mul(tdir, last.r * 0.62)), last.c, 0.5, 1);
  for (let k = 0; k < N; k++) B.tri(rings[rings.length - 1][k], rings[rings.length - 1][(k + 1) % N], capC);
  if (o.capBase) {
    const first = path[0];
    const bdir = norm(sub(path[0].p, path[1].p));
    const bC = B.vert(add(first.p, mul(bdir, first.r * 0.35)), first.c, 0.5, 0);
    for (let k = 0; k < N; k++) B.tri(rings[0][(k + 1) % N], rings[0][k], bC);
  }
}

/* ---------- species ---------- */
function specBranching(B, rnd, fbm, S) {
  const detail = S.detail;
  const RAD = Math.max(7, Math.round(9 * detail));
  const paths = [];
  function branch(p0, dir, length, rad, depth) {
    const steps = Math.max(2, Math.round(length / (S.size * 0.055) * detail));
    const path = [];
    let cur = p0.slice(), d = dir.slice();
    for (let i = 0; i <= steps; i++) {
      const f = i / steps;
      if (i > 0) {
        const w = [fbm([cur[0] * 3 + 11, cur[1] * 3, cur[2] * 3], 1, 2, 0.5),
                   fbm([cur[0] * 3, cur[1] * 3 + 23, cur[2] * 3], 1, 2, 0.5) * 0.4,
                   fbm([cur[0] * 3, cur[1] * 3, cur[2] * 3 + 37], 1, 2, 0.5)];
        d = norm(add(add(d, mul([0, 1, 0], S.tropism / steps)), mul(w, S.wander / steps)));
        cur = add(cur, mul(d, length / steps));
      }
      const r = rad * (1 - 0.34 * f) * (1 + 0.10 * fbm([cur[0] * 9, cur[1] * 9, cur[2] * 9], 1, 2, 0.5));
      const pale = depth === 0 ? Math.pow(f, 2.4) * 0.92 : 0;
      const warm = (1 - depth / S.depth) * 0.18;
      path.push({ p: cur, r: Math.max(r, S.size * 0.016),
                  c: mixc(mixc(S.pal.base, S.pal.tip, warm), S.pal.tip, pale) });
    }
    paths.push({ path: path, root: depth === S.depth });
    if (depth <= 0) return;
    const nb = 2 + (rnd() < S.trifurc ? 1 : 0);
    const childR = rad * Math.pow(1 / nb, 1 / (S.taperExp || 2.2));
    const around = perp(d), side = cross(d, around);
    const roll = rnd() * TAU;
    for (let k = 0; k < nb; k++) {
      const a = roll + k * 2.39996 + (rnd() - 0.5) * 0.5;
      const spread = S.spread * (0.72 + rnd() * 0.56);
      const off = add(mul(around, Math.cos(a)), mul(side, Math.sin(a)));
      const cd = norm(add(mul(d, Math.cos(spread)), mul(off, Math.sin(spread))));
      const start = add(cur, mul(d, -rad * 0.6));
      branch(start, cd, length * S.shrink * (0.82 + rnd() * 0.36), childR, depth - 1);
    }
  }
  branch([0, 0, 0], norm([(rnd() - 0.5) * 0.25, 1, (rnd() - 0.5) * 0.25]),
         S.size * S.trunk, S.size * S.radius, S.depth);
  for (const q of paths)
    tube(B, q.path, { radial: RAD, bump: S.bump, bumpFreq: 12 / S.size,
                      fbm: fbm, uvScale: 3 / S.size, capBase: q.root });
}

function specTable(B, rnd, fbm, S) {
  const detail = S.detail;
  const R = S.size * (0.80 + rnd() * 0.42), H = S.size * (0.30 + rnd() * 0.26);
  const tiltA = (0.06 + rnd() * 0.20), tiltD = rnd() * TAU;
  const domeK = 0.03 + rnd() * 0.11;
  const lobes = 2 + (rnd() * 3 | 0);
  const NS = Math.max(40, Math.round(104 * detail));
  const NR = Math.max(6, Math.round(14 * detail));
  const ribs = 9 + (rnd() * 7 | 0);
  const ph = rnd() * TAU;
  const stem = [];
  const steps = Math.max(3, Math.round(6 * detail));
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    stem.push({ p: [Math.sin(f * 2 + ph) * R * 0.02, f * H, Math.cos(f * 1.6) * R * 0.02],
                r: R * (0.11 - 0.045 * f), c: mulc(S.pal.base, 0.82) });
  }
  tube(B, stem, { radial: Math.max(7, Math.round(10 * detail)), bump: 0.09,
                  bumpFreq: 34 / S.size, fbm: fbm, uvScale: 2 / S.size, capBase: true });
  const outline = a => R * (0.78 + 0.34 * fbm([Math.cos(a) * 2.4, 3.1, Math.sin(a) * 2.4], 1, 2, 0.5)
                              + 0.10 * Math.sin(a * lobes + tiltD));
  function surf(a, f, sideSign) {
    const rr = outline(a) * f;
    const dome = H + R * domeK * (1 - f * f) - R * 0.02 * f * f
               + tiltA * rr * Math.cos(a - tiltD);
    const rib = R * 0.020 * Math.pow(f, 1.3) * Math.sin(a * ribs + ph + fbm([Math.cos(a) * 2, 1, Math.sin(a) * 2], 1, 2, 0.5) * 2);
    const wob = R * 0.035 * Math.pow(f, 2.0) * fbm([Math.cos(a) * 2.2, 7, Math.sin(a) * 2.2], 1, 3, 0.5);
    const th = R * 0.075 * (1 - 0.62 * f);
    const y = dome + rib + wob + sideSign * th * 0.5;
    return [Math.cos(a) * rr, y, Math.sin(a) * rr];
  }
  const top = [], bot = [];
  for (let i = 0; i <= NR; i++) {
    const f = i / NR, rt = [], rb = [];
    for (let j = 0; j < NS; j++) {
      const a = j / NS * TAU;
      const cT = mixc(S.pal.base, S.pal.tip, sstep(0.15, 0.95, f) * 0.75);
      const cB = mixc(mulc(S.pal.base, 0.62), S.pal.accent, sstep(0.4, 1.0, f) * 0.5);
      rt.push(B.vert(surf(a, f, +1), i === NR ? mixc(cT, S.pal.accent, 0.45) : cT, j / NS, f));
      rb.push(B.vert(surf(a, f, -1), cB, j / NS, f));
    }
    top.push(rt); bot.push(rb);
  }
  for (let i = 0; i < NR; i++) for (let j = 0; j < NS; j++) {
    const j2 = (j + 1) % NS;
    B.quad(top[i][j], top[i][j2], top[i + 1][j2], top[i + 1][j]);
    B.quad(bot[i][j2], bot[i][j], bot[i + 1][j], bot[i + 1][j2]);
  }
  for (let j = 0; j < NS; j++) {
    const j2 = (j + 1) % NS;
    B.quad(top[NR][j2], top[NR][j], bot[NR][j], bot[NR][j2]);
  }
}

function domeSurface(B, rnd, fbm, S, opts) {
  const detail = S.detail;
  const NU = Math.max(14, Math.round(opts.nu * detail));
  const NV = Math.max(24, Math.round(opts.nv * detail));
  const R = S.size, H = S.size * opts.squash;
  const grid = [];
  for (let i = 0; i <= NU; i++) {
    const u = i / NU, row = [];
    const pol = u * Math.PI * 0.5;
    for (let j = 0; j < NV; j++) {
      const a = j / NV * TAU;
      let dir = [Math.sin(pol) * Math.cos(a), Math.cos(pol), Math.sin(pol) * Math.sin(a)];
      const lump = 1 + opts.lump * fbm([dir[0] * 1.15, dir[1] * 1.15, dir[2] * 1.15], 1, 3, 0.5);
      let p = [dir[0] * R * lump, dir[1] * H * lump, dir[2] * R * lump];
      const nrm = norm([p[0] / (R * R), p[1] / (H * H), p[2] / (R * R)]);
      const det = opts.detailFn(p, nrm, dir, u);
      p = add(p, mul(nrm, det.d * sstep(1.0, 0.72, u)));
      row.push(B.vert(p, det.c, j / NV, u));
    }
    grid.push(row);
  }
  for (let i = 0; i < NU; i++) for (let j = 0; j < NV; j++) {
    const j2 = (j + 1) % NV;
    B.quad(grid[i][j], grid[i][j2], grid[i + 1][j2], grid[i + 1][j]);
  }
  const apex = B.vert([0, H * (1 + opts.lump * 0.3) + opts.apexLift, 0], opts.apexColor, 0.5, 0);
  for (let j = 0; j < NV; j++) B.tri(grid[0][(j + 1) % NV], grid[0][j], apex);
}

function specBrain(B, rnd, fbm, S) {
  const bands = 4.6 + rnd() * 2.2;
  const gd = S.size * (0.075 + rnd() * 0.030);
  const off = [rnd() * 40, rnd() * 40, rnd() * 40];
  domeSurface(B, rnd, fbm, S, {
    nu: 38, nv: 104, squash: 0.52 + rnd() * 0.14, lump: 0.13,
    apexLift: 0, apexColor: S.pal.tip,
    detailFn: (p) => {
      const q = [p[0] / S.size + off[0], p[1] / S.size * 1.35 + off[1], p[2] / S.size + off[2]];
      const f = fbm(q, 1.30, 2, 0.55);
      const m = Math.cos(f * TAU * bands);
      const valley = sstep(-0.15, 0.75, m);
      const grain = fbm([p[0] * 26, p[1] * 26, p[2] * 26], 1, 2, 0.5) * S.size * 0.006;
      const c = mixc(S.pal.tip, S.pal.base, valley);
      return { d: -gd * valley + grain, c: mulc(c, 1 - 0.30 * valley) };
    }
  });
}

function specMassive(B, rnd, fbm, S) {
  const off = [rnd() * 40, rnd() * 40, rnd() * 40];
  domeSurface(B, rnd, fbm, S, {
    nu: 34, nv: 76, squash: 0.66 + rnd() * 0.26, lump: 0.34,
    apexLift: 0, apexColor: S.pal.tip,
    detailFn: (p) => {
      const q = [p[0] / S.size + off[0], p[1] / S.size + off[1], p[2] / S.size + off[2]];
      const knob = fbm(q, 2.6, 3, 0.55);
      const fine = fbm(q, 8.5, 3, 0.5);
      const d = S.size * (0.145 * knob + 0.036 * fine);
      const c = mixc(S.pal.base, S.pal.tip, sstep(-0.3, 0.6, knob) * 0.7);
      return { d: d, c: mixc(c, S.pal.accent, sstep(0.1, -0.6, knob) * 0.5) };
    }
  });
}

function specFoliose(B, rnd, fbm, S) {
  const detail = S.detail;
  const N = 8 + (rnd() * 6 | 0);
  const NU = Math.max(20, Math.round(38 * detail));
  const NV = Math.max(4, Math.round(7 * detail));
  for (let k = 0; k < N; k++) {
    const t0 = k / N;
    const a0 = rnd() * TAU;
    const span = 1.1 + rnd() * 1.0;
    const r0 = S.size * (0.10 + 0.22 * t0);
    const L = S.size * (0.30 + 0.30 * (1 - t0) + rnd() * 0.14);
    const y0 = S.size * (0.05 + 0.62 * t0);
    const rise = S.size * (0.26 + rnd() * 0.30);
    const ruff = 3.5 + rnd() * 4.5;
    const amp = S.size * (0.055 + rnd() * 0.055);
    const ph = rnd() * TAU;
    const tilt = 0.42 + rnd() * 0.34;
    const top = [], bot = [];
    for (let i = 0; i <= NU; i++) {
      const u = i / NU, ang = a0 + (u - 0.5) * span;
      const rt = [], rb = [];
      for (let j = 0; j <= NV; j++) {
        const v = j / NV;
        const rad = r0 + L * v;
        const y = y0 + rise * Math.pow(v, 0.75) + amp * Math.pow(v, 1.7) *
                  Math.sin(u * ruff * span + ph + fbm([Math.cos(ang) * 2, v * 3, Math.sin(ang) * 2], 1, 2, 0.5) * 2.2);
        const lean = tilt * v * rad;
        const p = [Math.cos(ang) * (rad + lean * 0.1), y, Math.sin(ang) * (rad + lean * 0.1)];
        const th = S.size * 0.020 * (1 - 0.72 * v);
        const nrm = norm([Math.cos(ang) * 0.25, 1, Math.sin(ang) * 0.25]);
        const cT = mixc(S.pal.base, S.pal.tip, sstep(0.25, 1.0, v));
        const cB = mixc(mulc(S.pal.base, 0.55), S.pal.accent, 0.4);
        rt.push(B.vert(add(p, mul(nrm, th)), cT, u, v));
        rb.push(B.vert(add(p, mul(nrm, -th)), cB, u, v));
      }
      top.push(rt); bot.push(rb);
    }
    for (let i = 0; i < NU; i++) for (let j = 0; j < NV; j++) {
      B.quad(top[i][j], top[i][j + 1], top[i + 1][j + 1], top[i + 1][j]);
      B.quad(bot[i][j + 1], bot[i][j], bot[i + 1][j], bot[i + 1][j + 1]);
    }
    for (let i = 0; i < NU; i++) B.quad(top[i][NV], bot[i][NV], bot[i + 1][NV], top[i + 1][NV]);
    for (let j = 0; j < NV; j++) {
      B.quad(top[0][j + 1], bot[0][j + 1], bot[0][j], top[0][j]);
      B.quad(bot[NU][j + 1], top[NU][j + 1], top[NU][j], bot[NU][j]);
    }
  }
  const stem = [];
  for (let i = 0; i <= 4; i++) {
    const f = i / 4;
    stem.push({ p: [0, f * S.size * 0.16, 0], r: S.size * (0.10 - 0.045 * f), c: mulc(S.pal.base, 0.6) });
  }
  tube(B, stem, { radial: 9, bump: 0.12, bumpFreq: 30 / S.size, fbm: fbm, uvScale: 2 / S.size, capBase: true });
}

function specFan(B, rnd, fbm, S) {
  const detail = S.detail;
  const RAD = Math.max(4, Math.round(5 * detail));
  const paths = [];
  const H = S.size * 1.25, W = S.size;
  function branch(p0, dir, length, rad, depth) {
    const steps = Math.max(2, Math.round(length / (S.size * 0.07) * detail));
    const path = [];
    let cur = p0.slice(), d = dir.slice();
    for (let i = 0; i <= steps; i++) {
      const f = i / steps;
      if (i > 0) {
        const want = norm([cur[0] * 2.6, Math.max(cur[1], H * 0.05) + H * 0.26, 0]);
        d = norm(add(add(d, mul(want, 1.5 / steps)),
                     mul([fbm([cur[0] * 4, cur[1] * 4, 5], 1, 2, 0.5), 0,
                          fbm([cur[0] * 4, cur[1] * 4, 9], 1, 2, 0.5) * 0.25], 0.7 / steps)));
        d[2] *= 0.35;
        d = norm(d);
        cur = add(cur, mul(d, length / steps));
        cur[2] = Math.sin(cur[0] / W * 2.1) * S.size * 0.07 + fbm([cur[0] * 2, cur[1] * 2, 3], 1, 2, 0.5) * S.size * 0.03;
      }
      const tint = clamp(cur[1] / H, 0, 1);
      path.push({ p: cur, r: Math.max(rad * (1 - 0.4 * f), S.size * 0.0035),
                  c: mixc(S.pal.base, S.pal.tip, tint * tint) });
    }
    paths.push({ path: path, root: depth === 8 });
    if (depth <= 0) return;
    const childR = rad * Math.pow(0.5, 1 / (S.taperExp || 2.3));
    for (let k = 0; k < 2; k++) {
      const sgn = k === 0 ? -1 : 1;
      const spread = 0.30 + rnd() * 0.30;
      const cd = norm([d[0] * Math.cos(spread) - d[1] * Math.sin(spread) * sgn,
                       d[1] * Math.cos(spread) + d[0] * Math.sin(spread) * sgn, d[2]]);
      branch(add(cur, mul(d, -rad * 0.5)), cd, length * 0.80 * (0.85 + rnd() * 0.3), childR, depth - 1);
    }
  }
  branch([0, 0, 0], [0, 1, 0], S.size * 0.26, S.size * 0.050, 8);
  for (const q of paths)
    tube(B, q.path, { radial: RAD, bump: S.bump * 1.2, bumpFreq: 12 / S.size,
                      fbm: fbm, uvScale: 4 / S.size, capBase: q.root });
}

const SPEC = { branching: specBranching, table: specTable, brain: specBrain,
               massive: specMassive, foliose: specFoliose, fan: specFan };
const SPECIES = Object.keys(SPEC);

/* ---------- ambient occlusion ---------- */
const AO_DIRS = (() => {
  const d = [], n = 10;
  for (let i = 0; i < n; i++) {
    const y = 1 - (i + 0.5) / n * 0.92;
    const r = Math.sqrt(Math.max(0, 1 - y * y)), th = i * 2.39996;
    d.push([Math.cos(th) * r, y, Math.sin(th) * r]);
  }
  return d;
})();

function bakeAO(mesh, strength, size) {
  const P = mesh.positions, N = mesh.normals, C = mesh.colors, I = mesh.indices;
  let x0 = 1e9, y0 = 1e9, z0 = 1e9, x1 = -1e9, y1 = -1e9, z1 = -1e9;
  for (let i = 0; i < P.length; i += 3) {
    if (P[i] < x0) x0 = P[i]; if (P[i] > x1) x1 = P[i];
    if (P[i + 1] < y0) y0 = P[i + 1]; if (P[i + 1] > y1) y1 = P[i + 1];
    if (P[i + 2] < z0) z0 = P[i + 2]; if (P[i + 2] > z1) z1 = P[i + 2];
  }
  const pad = size * 0.05;
  x0 -= pad; y0 -= pad; z0 -= pad; x1 += pad; y1 += pad; z1 += pad;
  const G = 44;
  const sx = G / Math.max(x1 - x0, 1e-6), sy = G / Math.max(y1 - y0, 1e-6), sz = G / Math.max(z1 - z0, 1e-6);
  const occ = new Uint8Array(G * G * G);
  const cell = (x, y, z) => {
    const i = (x - x0) * sx | 0, j = (y - y0) * sy | 0, k = (z - z0) * sz | 0;
    if (i < 0 || i >= G || j < 0 || j >= G || k < 0 || k >= G) return -1;
    return (i * G + j) * G + k;
  };
  for (let i = 0; i < P.length; i += 3) { const c = cell(P[i], P[i + 1], P[i + 2]); if (c >= 0) occ[c] = 1; }
  for (let k = 0; k < I.length; k += 3) {
    const a = I[k] * 3, b = I[k + 1] * 3, c = I[k + 2] * 3;
    const q = cell((P[a] + P[b] + P[c]) / 3, (P[a + 1] + P[b + 1] + P[c + 1]) / 3, (P[a + 2] + P[b + 2] + P[c + 2]) / 3);
    if (q >= 0) occ[q] = 1;
  }
  const step = size * 0.055, K = AO_DIRS.length;
  for (let v = 0; v < P.length; v += 3) {
    const n = [N[v], N[v + 1], N[v + 2]];
    const t = perp(n), b = cross(n, t);
    let hit = 0;
    for (let k = 0; k < K; k++) {
      const d = AO_DIRS[k];
      const dir = [t[0] * d[0] + n[0] * d[1] + b[0] * d[2],
                   t[1] * d[0] + n[1] * d[1] + b[1] * d[2],
                   t[2] * d[0] + n[2] * d[1] + b[2] * d[2]];
      for (let s = 1; s <= 4; s++) {
        const l = step * s * 1.6;
        const c = cell(P[v] + dir[0] * l + n[0] * step * 0.5,
                       P[v + 1] + dir[1] * l + n[1] * step * 0.5,
                       P[v + 2] + dir[2] * l + n[2] * step * 0.5);
        if (c >= 0 && occ[c]) { hit += 1 - (s - 1) * 0.22; break; }
      }
    }
    const ao = clamp(1 - hit / K, 0, 1);
    const f = lerp(1 - strength, 1, ao * ao);
    C[v] *= f; C[v + 1] *= f; C[v + 2] *= f;
  }
}

/* ---------- build ---------- */
const DEFAULTS = {
  branching: { trunk: 0.46, radius: 0.098, depth: 5, spread: 0.80, shrink: 0.70,
               tropism: 0.60, wander: 0.50, trifurc: 0.55, bump: 0.13, taperExp: 5.5, ao: 0.42 },
  table:     { bump: 0.09, ao: 0.45 },
  brain:     { bump: 0, ao: 0.62 },
  massive:   { bump: 0, ao: 0.50 },
  foliose:   { bump: 0, ao: 0.58 },
  fan:       { trunk: 0.34, radius: 0.045, bump: 0.10, taperExp: 2.3, ao: 0.35 }
};

function build(opts) {
  const o = opts || {};
  const species = SPEC[o.species] ? o.species : 'branching';
  const seed = o.seed !== undefined ? o.seed : (Math.random() * 1e6) | 0;
  const size = o.size !== undefined ? o.size : 1.0;
  const detail = clamp(o.detail !== undefined ? o.detail : 1.0, 0.25, 3.0);
  const bleach = clamp(o.bleach || 0, 0, 1);
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const rnd = mulberry32(seed >>> 0);
  const nz = makeNoise3(seed * 2654435761 + 17);
  const fbm = makeFbm3(nz);
  let pal = o.palette || PALETTES[species];
  if (bleach > 0) pal = { base: bleachC(pal.base, bleach), tip: bleachC(pal.tip, bleach),
                          accent: bleachC(pal.accent, bleach) };
  const S = Object.assign({ size, detail, pal, bump: 0 }, DEFAULTS[species], o.params || {});
  S.size = size; S.detail = detail; S.pal = pal;
  const B = new Builder();
  SPEC[species](B, rnd, fbm, S);
  const mesh = B.finish();
  const aoStrength = o.ao !== undefined ? o.ao : S.ao;
  if (aoStrength > 0) bakeAO(mesh, aoStrength, size);
  const P = mesh.positions;
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9, z0 = 1e9, z1 = -1e9;
  for (let i = 0; i < P.length; i += 3) {
    if (P[i] < x0) x0 = P[i]; if (P[i] > x1) x1 = P[i];
    if (P[i + 1] < y0) y0 = P[i + 1]; if (P[i + 1] > y1) y1 = P[i + 1];
    if (P[i + 2] < z0) z0 = P[i + 2]; if (P[i + 2] > z1) z1 = P[i + 2];
  }
  const cx = (x0 + x1) * 0.5, cz = (z0 + z1) * 0.5;
  for (let i = 0; i < P.length; i += 3) { P[i] -= cx; P[i + 1] -= y0; P[i + 2] -= cz; }
  mesh.species = species; mesh.seed = seed; mesh.size = size;
  mesh.verts = P.length / 3; mesh.tris = mesh.indices.length / 3;
  mesh.bounds = { w: x1 - x0, h: y1 - y0, d: z1 - z0 };
  mesh.ms = (typeof performance !== 'undefined' ? performance : Date).now() - t0;
  return mesh;
}

function toBufferGeometry(THREE, m) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
  g.setAttribute('color', new THREE.BufferAttribute(m.colors, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(m.uvs, 2));
  g.setIndex(new THREE.BufferAttribute(m.indices, 1));
  g.computeBoundingSphere();
  return g;
}

const Coral = { build, toBufferGeometry, SPECIES, PALETTES, DEFAULTS };

export { build, toBufferGeometry, SPECIES, PALETTES, DEFAULTS };
export default Coral;
